/**
 * LDS HYP payment outcome — business order code for display (never raw UUID/deal token).
 */
import {
  formatHrLabel,
  isLikelyOpaqueLdsDealToken,
  resolveCustomerFacingOfferNumber,
} from "./resolveCustomerFacingOfferNumber.js";
import { buildOrganizedTourOrderDisplayFallback } from "./resolveOrganizedTourDisplayOrderNumber.js";

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** @param {unknown} v */
function trimStr(v) {
  if (v == null) return "";
  if (typeof v === "number" && Number.isFinite(v)) return String(v).trim();
  if (typeof v === "string") return v.trim();
  return "";
}

/** @param {unknown} s */
function isForbiddenTechnicalToken(s) {
  const t = trimStr(s);
  if (!t) return true;
  if (UUID_RE.test(t)) return true;
  if (isLikelyOpaqueLdsDealToken(t)) return true;
  if (t.startsWith("eyJ")) return true;
  if (t.length > 64) return true;
  return false;
}

/** @param {unknown} raw */
function normalizeBusinessCode(raw) {
  const t = trimStr(raw);
  if (!t || isForbiddenTechnicalToken(t)) return null;
  const compact = t.replace(/\s/g, "");
  if (/^HR-[1-9]\d{7}$/i.test(compact)) {
    return compact.replace(/^hr-/i, "HR-");
  }
  const hr = formatHrLabel(t);
  if (hr) return hr;
  if (t.length >= 3 && t.length <= 24 && /^[A-Za-z0-9\-_/]+$/.test(t)) {
    return t;
  }
  return null;
}

function isDevRuntime() {
  if (typeof import.meta !== "undefined" && import.meta?.env?.DEV) return true;
  if (typeof process !== "undefined" && process?.env?.NODE_ENV) {
    return process.env.NODE_ENV !== "production";
  }
  return false;
}

function emitDisplayOrderCodeTrace(payload) {
  if (!isDevRuntime()) return;
  try {
    // eslint-disable-next-line no-console
    console.info("[LDS_SUCCESS_DISPLAY_ORDER_CODE_TRACE]", payload);
  } catch {
    /* ignore */
  }
}

/**
 * @param {object|null|undefined} order
 * @param {string|null|undefined} technicalOrderId
 * @returns {{ selectedDisplayCode: string, sourcePath: string, fallbackUsed: boolean }}
 */
export function resolveLdsSuccessDisplayOrderCode(order, technicalOrderId = null) {
  const tech =
    trimStr(technicalOrderId) ||
    trimStr(order?.id) ||
    trimStr(order?.orderid) ||
    null;

  const tryPick = (raw, sourcePath) => {
    const code = normalizeBusinessCode(raw);
    if (!code) return null;
    return { selectedDisplayCode: code, sourcePath, fallbackUsed: false };
  };

  const hrFields = [
    [order?.hr_order_number, "order.hr_order_number"],
    [order?.hrOrderNumber, "order.hrOrderNumber"],
    [order?.display_order_number, "order.display_order_number"],
    [order?.displayOrderNumber, "order.displayOrderNumber"],
    [order?.display_order_id, "order.display_order_id"],
    [order?.displayOrderId, "order.displayOrderId"],
    [order?.booking_ref_display, "order.booking_ref_display"],
    [order?.bookingRefDisplay, "order.bookingRefDisplay"],
  ];

  for (const [raw, sourcePath] of hrFields) {
    const hit = tryPick(raw, sourcePath);
    if (hit) {
      emitDisplayOrderCodeTrace({
        orderId: tech,
        selectedDisplayCode: hit.selectedDisplayCode,
        sourcePath: hit.sourcePath,
        fallbackUsed: false,
      });
      return hit;
    }
  }

  const facing = resolveCustomerFacingOfferNumber(order);
  if (facing.show && facing.value) {
    const hit = tryPick(facing.value, `resolveCustomerFacingOfferNumber.${facing.kind}`);
    if (hit) {
      emitDisplayOrderCodeTrace({
        orderId: tech,
        selectedDisplayCode: hit.selectedDisplayCode,
        sourcePath: hit.sourcePath,
        fallbackUsed: false,
      });
      return hit;
    }
  }

  const op = order?.ourprice ?? order?.ourPrice ?? {};
  const cqd = op?.customerQuoteDisplay ?? op?.customer_quote_display;
  const offerCandidates = [
    [op?.offerNumber, "ourprice.offerNumber"],
    [op?.offer_number, "ourprice.offer_number"],
    [cqd?.offerNumber, "ourprice.customerQuoteDisplay.offerNumber"],
    [cqd?.offer_number, "ourprice.customerQuoteDisplay.offer_number"],
    [order?.offerNumber, "order.offerNumber"],
    [order?.offer_number, "order.offer_number"],
  ];
  for (const [raw, sourcePath] of offerCandidates) {
    const hit = tryPick(raw, sourcePath);
    if (hit) {
      emitDisplayOrderCodeTrace({
        orderId: tech,
        selectedDisplayCode: hit.selectedDisplayCode,
        sourcePath: hit.sourcePath,
        fallbackUsed: false,
      });
      return hit;
    }
  }

  const fb = buildOrganizedTourOrderDisplayFallback(tech);
  const out = {
    selectedDisplayCode: fb,
    sourcePath: "fallback.hr_compact",
    fallbackUsed: true,
  };
  emitDisplayOrderCodeTrace({
    orderId: tech,
    selectedDisplayCode: out.selectedDisplayCode,
    sourcePath: out.sourcePath,
    fallbackUsed: true,
  });
  return out;
}
